import { ChangeEventHandler, MouseEventHandler, useState } from "react";

import { useQueryClient } from "@enkor/react-query";

import { useCreateEventUnregisterMutation } from "@domains/event/network/eventMutations";
import { GA_EVENT_IDS } from "@domains/common/constants/googleAnalyticsIds";
import useModalState from "@logics/hooks/useModalState";
import { eventQueryKeys } from "@domains/event/constants/eventQueryKeys";
import { myPageQueryKeys } from "@domains/myPage/constants/myPageQueryKeys";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { EVENT_MESSAGE } from "@domains/event/constants/toastMessages";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import { EventInfoTrackingDataType } from "@domains/event/types/event";
import ReasonOptionSelect from "@domains/event/views/components/ReasonOptionSelect/ReasonOptionSelect";

import * as S from "./CancelReasonBottomSheet.style";

const CANCEL_REASON = {
  PERSONAL_SCHEDULE_CONFLICT: "Personal Schedule Conflict",
  FINANCIAL_ISSUES: "Financial Issues",
  MISTAKEN_REGISTRATION: "Mistaken Registration",
};

interface CancelReasonBottomSheetProps {
  eventId: string;
  eventInfo: EventInfoTrackingDataType;
}

const CancelReasonBottomSheet = ({
  eventId,
  eventInfo,
}: CancelReasonBottomSheetProps) => {
  const queryClient = useQueryClient();

  const [selectedReason, setSelectedReason] = useState("");
  const [reasonContent, setReasonContent] = useState("");

  const { closeModal } = useModalState();

  const unregisterMutation = useCreateEventUnregisterMutation();

  const handleSelectReason = (reasonOption: string) => {
    setSelectedReason(reasonOption);
    setReasonContent("");
  };

  const handleChangeTextArea: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setReasonContent(e.target.value);
  };

  const handleSubmitReport: MouseEventHandler<HTMLButtonElement> = async (
    e,
  ) => {
    e.preventDefault();

    if (!selectedReason || unregisterMutation.isPending) return;

    try {
      await unregisterMutation.mutateAsync({
        eventId,
        reason: reasonContent.trim() || selectedReason, // NOTE: etc가 선택된 경우 textarea에 내용이 없으면 선택된 이유로 보냄
      });

      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.getEventDetail({ eventId }),
      });
      queryClient.invalidateQueries({
        queryKey: myPageQueryKeys.getEventList({ participating: true }),
      });

      ToastSuccess(EVENT_MESSAGE.JOIN_CANCELLED);

      dataTrackEvent({
        eventName:
          DATA_TRACKING_EVENT_NAME.GUEST_EVENT.CLICK_FINISH_EVENT_LEAVE,
        properties: eventInfo,
      });

      closeModal();
    } catch (error) {
      ToastError(ERROR_MESSAGE.SERVER_ERROR);
    }
  };

  return (
    <S.BottomSheetContainer>
      <S.HeaderWrapper>
        <S.Title name="m">Cancel event participation</S.Title>
        <S.CancelButton type="button" onClick={closeModal}>
          Cancel
        </S.CancelButton>
      </S.HeaderWrapper>
      <S.ScrollWrapper>
        <S.TextareaWrapper>
          <S.TextareaDescription name="strongS">
            Are you sure you want to cancel your participation in the event?
          </S.TextareaDescription>

          <ReasonOptionSelect
            selectedOption={selectedReason}
            checkboxObject={CANCEL_REASON}
            etcTextarea={{
              labelText: "Other Reasons",
              labelKey: "etc",
              placeholder:
                "Please provide the reason for canceling your participation.",
              value: reasonContent,
              onChange: handleChangeTextArea,
            }}
            onSelectOption={handleSelectReason}
          />
        </S.TextareaWrapper>
        <S.SubmitButton
          type="button"
          id={GA_EVENT_IDS.EVENT.GUEST_FINISH_LEAVE}
          disabled={
            !selectedReason ||
            (selectedReason === "etc" && !reasonContent.trim()) // NOTE: reason이 선택되지 않았거나, etc가 선택된 경우 textarea에 내용이 없으면 비활성화
          }
          onClick={handleSubmitReport}
        >
          Cancel event participation
        </S.SubmitButton>
      </S.ScrollWrapper>
    </S.BottomSheetContainer>
  );
};

export default CancelReasonBottomSheet;
